<script setup lang="ts">
/**
 * FilterBuilderPanel
 *
 * Layer 2 — Advanced AND/OR filter builder with nested groups.
 * Uses useFilterBuilder composable for tree management.
 */

import { ref, onMounted } from 'vue'
import FilterConditionRow from './FilterConditionRow.vue'
import type { FilterCondition, FilterGroup, SchemaField, SavedViewEntityType } from '../api/types'
import { isFilterCondition, isFilterGroup } from '../api/types'
import { useFilterBuilder } from '../composables/useFilterBuilder'
import { savedViewService } from '../api/service'

interface Props {
    entityType: SavedViewEntityType
    fields: SchemaField[]
    initialFilters?: FilterGroup
}

const props = defineProps<Props>()

const emit = defineEmits<{
    apply: [filters: FilterGroup]
    cancel: []
}>()

const {
    filterTree,
    addCondition,
    addGroup,
    removeNode,
    updateCondition,
    toggleGroupOperator,
    conditionCount,
    exceedsMaxConditions,
    isValid,
    toCleanTree,
    setTree,
} = useFilterBuilder(props.initialFilters)

const previewCount = ref<number | null>(null)
const isLoadingPreview = ref(false)
let previewTimeout: ReturnType<typeof setTimeout> | null = null

/** Debounced preview count */
function requestPreview() {
    if (previewTimeout) clearTimeout(previewTimeout)
    previewTimeout = setTimeout(async () => {
        if (!isValid.value) {
            previewCount.value = null
            return
        }
        const cleanTree = toCleanTree()
        if (!cleanTree) return

        isLoadingPreview.value = true
        try {
            const result = await savedViewService.preview({
                entityType: props.entityType,
                filters: cleanTree,
                limit: 0,
            })
            previewCount.value = result.meta.total
        } catch {
            previewCount.value = null
        } finally {
            isLoadingPreview.value = false
        }
    }, 500)
}

function onApply() {
    const cleanTree = toCleanTree()
    if (cleanTree) {
        emit('apply', cleanTree)
    }
}

// Watch for changes and auto-preview
onMounted(() => {
    if (props.initialFilters) {
        setTree(props.initialFilters)
    }
})
</script>

<template>
    <div class="builder-panel">
        <div class="builder-panel__header">
            <h4 class="builder-panel__title">Advanced Filters</h4>
            <div class="builder-panel__stats">
                <span v-if="isLoadingPreview" class="builder-panel__preview">Counting…</span>
                <span v-else-if="previewCount !== null" class="builder-panel__preview">
                    {{ previewCount.toLocaleString() }} result{{ previewCount !== 1 ? 's' : '' }}
                </span>
                <span class="builder-panel__condition-count">
                    {{ conditionCount }} / 50 conditions
                </span>
            </div>
        </div>

        <!-- Recursive group renderer -->
        <div class="builder-panel__tree">
            <component
                :is="'filter-group-node'"
                v-if="false"
            />

            <!-- Root group -->
            <div class="group group--root">
                <div class="group__header">
                    <button
                        class="group__operator-toggle"
                        role="switch"
                        :aria-checked="filterTree.operator === 'OR'"
                        @click="toggleGroupOperator([]); requestPreview()"
                    >
                        <span :class="['toggle-option', { 'toggle-option--active': filterTree.operator === 'AND' }]">AND</span>
                        <span :class="['toggle-option', { 'toggle-option--active': filterTree.operator === 'OR' }]">OR</span>
                    </button>
                </div>

                <div class="group__conditions">
                    <template v-for="(child, index) in filterTree.conditions" :key="index">
                        <!-- Leaf condition -->
                        <FilterConditionRow
                            v-if="isFilterCondition(child)"
                            :condition="child as FilterCondition"
                            :fields="fields"
                            :entity-type="entityType"
                            @update="(updates) => { updateCondition([index], updates); requestPreview() }"
                            @remove="removeNode([index]); requestPreview()"
                        />

                        <!-- Nested group (1 level) -->
                        <div v-else-if="isFilterGroup(child)" class="group group--nested">
                            <div class="group__header">
                                <button
                                    class="group__operator-toggle group__operator-toggle--sm"
                                    @click="toggleGroupOperator([index]); requestPreview()"
                                >
                                    <span :class="['toggle-option', { 'toggle-option--active': (child as FilterGroup).operator === 'AND' }]">AND</span>
                                    <span :class="['toggle-option', { 'toggle-option--active': (child as FilterGroup).operator === 'OR' }]">OR</span>
                                </button>
                                <button class="group__remove" @click="removeNode([index]); requestPreview()" title="Remove group">×</button>
                            </div>

                            <div class="group__conditions">
                                <template v-for="(nestedChild, nestedIndex) in (child as FilterGroup).conditions" :key="nestedIndex">
                                    <FilterConditionRow
                                        v-if="isFilterCondition(nestedChild)"
                                        :condition="nestedChild as FilterCondition"
                                        :fields="fields"
                                        :entity-type="entityType"
                                        @update="(updates) => { updateCondition([index, nestedIndex], updates); requestPreview() }"
                                        @remove="removeNode([index, nestedIndex]); requestPreview()"
                                    />
                                </template>
                            </div>

                            <button
                                class="group__add-condition"
                                :disabled="exceedsMaxConditions"
                                @click="addCondition([index])"
                            >
                                + Add condition
                            </button>
                        </div>
                    </template>
                </div>

                <div class="group__actions">
                    <button
                        class="group__add-condition"
                        :disabled="exceedsMaxConditions"
                        @click="addCondition([])"
                    >
                        + Add condition
                    </button>
                    <button
                        class="group__add-group"
                        :disabled="exceedsMaxConditions"
                        @click="addGroup([])"
                    >
                        + Add group
                    </button>
                </div>
            </div>
        </div>

        <div v-if="exceedsMaxConditions" class="builder-panel__warning">
            Maximum of 50 conditions reached.
        </div>

        <div class="builder-panel__footer">
            <button class="btn btn--secondary" @click="emit('cancel')">Cancel</button>
            <button class="btn btn--primary" :disabled="!isValid" @click="onApply">Apply Filters</button>
        </div>
    </div>
</template>

<style scoped>
.builder-panel {
    background: var(--bg-elevated);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-lg);
    margin-top: 0.5rem;
}

.builder-panel__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--border-color);
}

.builder-panel__title {
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
}

.builder-panel__stats {
    display: flex;
    gap: 1rem;
    font-size: var(--text-xs);
    color: var(--text-muted);
}

.builder-panel__preview {
    color: var(--color-primary);
    font-weight: 600;
}

.builder-panel__tree {
    padding: 1rem;
}

.group {
    border-radius: var(--radius-md);
}

.group--nested {
    border-left: 3px solid var(--color-primary);
    padding-left: 1rem;
    margin: 0.5rem 0;
    background: rgba(18, 192, 221, 0.03);
    border-radius: 0 var(--radius-md) var(--radius-md) 0;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    padding-right: 0.5rem;
}

.group__header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
}

.group__operator-toggle {
    display: inline-flex;
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    overflow: hidden;
    background: none;
    cursor: pointer;
    padding: 0;
}

.group__operator-toggle--sm {
    font-size: var(--text-xs);
}

.toggle-option {
    padding: 0.25rem 0.625rem;
    font-size: var(--text-xs);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-muted);
    background: var(--bg-surface);
    transition: all 0.15s;
}

.toggle-option--active {
    color: #fff;
    background: var(--color-primary);
}

.group__remove {
    margin-left: auto;
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    color: var(--text-muted);
    background: none;
    border: none;
    border-radius: var(--radius-full);
    cursor: pointer;
}

.group__remove:hover {
    color: var(--color-danger);
    background: rgba(239, 68, 68, 0.1);
}

.group__conditions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.group__actions {
    display: flex;
    gap: 0.75rem;
    margin-top: 0.75rem;
}

.group__add-condition,
.group__add-group {
    font-size: var(--text-xs);
    color: var(--color-primary);
    background: none;
    border: 1px dashed var(--border-color);
    border-radius: var(--radius-md);
    padding: 0.375rem 0.75rem;
    cursor: pointer;
    transition: all 0.15s;
}

.group__add-condition:hover,
.group__add-group:hover {
    border-color: var(--color-primary);
    background: rgba(18, 192, 221, 0.05);
}

.group__add-condition:disabled,
.group__add-group:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.builder-panel__warning {
    padding: 0.5rem 1rem;
    font-size: var(--text-xs);
    color: var(--color-warning);
    background: rgba(245, 158, 11, 0.08);
    border-top: 1px solid rgba(245, 158, 11, 0.15);
}

.builder-panel__footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    border-top: 1px solid var(--border-color);
}

.btn {
    padding: 0.375rem 1rem;
    font-size: var(--text-sm);
    font-weight: 600;
    border-radius: var(--radius-md);
    border: none;
    cursor: pointer;
    transition: opacity 0.15s;
}

.btn--primary {
    color: #fff;
    background: var(--color-primary);
}

.btn--primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn--secondary {
    color: var(--text-secondary);
    background: var(--bg-surface);
    border: 1px solid var(--border-color);
}

.btn--secondary:hover {
    background: var(--bg-hover);
}
</style>
